// src/sections/Hero.js
import React, { useEffect, useRef, useState, lazy, Suspense } from 'react';
import styled from 'styled-components';
import { colors, fonts, fontSizes, fontWeights, mq, space, ease, duration, radii, shadows } from '../styles/tokens';
import { ButtonLink } from '../components/Button';
import { Icon } from '../components/Icon';
import { useTypewriter } from '../hooks/useTypewriter';
import { useReducedMotion } from '../hooks/useReducedMotion';
import { useMagnetic } from '../hooks/useMagnetic';

const LazySpline = lazy(() => import('@splinetool/react-spline'));

const Section = styled.section`
  position: relative;
  min-height: 100vh;
  padding: ${space[10]} ${space[4]} ${space[8]};
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;
  ${mq.md} { padding: ${space[12]} ${space[5]} ${space[10]}; }
`;

const Content = styled.div`
  position: relative;
  z-index: 2;
  max-width: 720px;
  width: 100%;
  margin: 0 auto;
  ${mq.lg} { margin: 0; padding-left: ${space[5]}; }
`;

const Prompt = styled.div`
  font-family: ${fonts.mono};
  font-size: ${fontSizes.xs};
  color: ${colors.accent};
  margin-bottom: ${space[3]};
  letter-spacing: 0.05em;
  min-height: 1em;
  .caret {
    display: inline-block;
    width: 0.5ch;
    background: ${colors.accent};
    margin-left: 2px;
    animation: blink-caret 0.8s step-end infinite;
  }
`;

const Headline = styled.h1`
  font-family: ${fonts.sans};
  font-size: ${fontSizes.hero};
  font-weight: ${fontWeights.black};
  line-height: 1.05;
  letter-spacing: -0.02em;
  color: ${colors.text};
  margin-bottom: ${space[2]};
`;

const Subtitle = styled.h2`
  font-family: ${fonts.sans};
  font-size: ${fontSizes['2xl']};
  font-weight: ${fontWeights.semibold};
  line-height: 1.2;
  letter-spacing: -0.01em;
  color: ${colors.text};
  margin-bottom: ${space[4]};

  .accent {
    background: ${colors.gradText};
    background-size: 200% 100%;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
    animation: gradient-pan 8s ${ease.soft} infinite;
  }
`;

const CTARow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${space[2]};
`;

const Orb = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  opacity: 0.3;
  pointer-events: none;
  ${mq.lg} {
    inset: auto 0 0 auto;
    width: 48%;
    height: 100%;
    opacity: 1;
    pointer-events: auto;
    padding: ${space[6]};
  }
`;

const TerminalFrame = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  ${mq.lg} {
    background: ${colors.bgElev};
    border: 1px solid ${colors.border};
    border-radius: ${radii.lg};
    box-shadow: ${shadows.cardHover};
    overflow: hidden;
  }
`;

const TitleBar = styled.div`
  display: none;
  ${mq.lg} {
    display: flex;
    align-items: center;
    gap: ${space[3]};
    height: 32px;
    padding: 0 ${space[3]};
    background: ${colors.bg};
    border-bottom: 1px solid ${colors.border};
    flex-shrink: 0;
  }
`;

const TrafficLights = styled.div`
  display: flex;
  gap: 6px;
`;

const Dot = styled.span`
  width: 12px;
  height: 12px;
  border-radius: ${radii.full};
  background: ${colors.textMuted};
  opacity: 0.4;
`;
//red dot
const CloseDot = styled.button`
  width: 12px;
  height: 12px;
  border-radius: ${radii.full};
  background: ${colors.textMuted};
  opacity: 0.4;
  border: none;
  padding: 0;
  cursor: pointer;
  transition:
    background ${duration.fast} ${ease.soft},
    opacity ${duration.fast} ${ease.soft};
  &:hover,
  &:focus-visible {
    background: #ff5f57;
    opacity: 1;
  }
  &:focus-visible {
    outline: 2px solid ${colors.accent};
    outline-offset: 2px;
  }
`;

const TitleLabel = styled.span`
  margin-left: auto;
  font-family: ${fonts.mono};
  font-size: ${fontSizes.xs};
  color: ${colors.textMuted};
  letter-spacing: 0.02em;
`;

const Screen = styled.div`
  flex: 1;
  position: relative;
  min-height: 0;
`;

const SplineWrap = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
`;

const LoadingState = styled.div`
  display: none;
  ${mq.lg} {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: ${fonts.mono};
    font-size: ${fontSizes.sm};
    color: ${colors.textMuted};
    letter-spacing: 0.02em;
    .caret {
      display: inline-block;
      width: 0.5ch;
      height: 1em;
      background: ${colors.accent};
      margin-left: 4px;
      vertical-align: text-bottom;
      animation: blink-caret 0.8s step-end infinite;
    }
  }
`;

const ScrollCue = styled.div`
  position: absolute;
  bottom: ${space[4]};
  right: ${space[5]};
  font-family: ${fonts.mono};
  font-size: ${fontSizes.xs};
  color: ${colors.textMuted};
  opacity: ${(p) => (p.$faded ? 0 : 1)};
  transition: opacity ${duration.slow} ${ease.soft};
  display: none;
  ${mq.md} { display: block; }
`;

const PROMPT_TEXT = '~ fabio@stockholm $ whoami';

const Hero = () => {
  const reduced = useReducedMotion();
  const { typed, isDone } = useTypewriter(PROMPT_TEXT, !reduced, 55);
  const [faded, setFaded] = useState(false);
  const [sceneOpen, setSceneOpen] = useState(true);
  const orbRef = useRef(null);
  const primaryBtnRef = useMagnetic({ strength: 4 });

  useEffect(() => {
    if (reduced) return;
    if (window.matchMedia('(hover: none)').matches) return;
    const onMove = (e) => {
      const el = orbRef.current;
      if (!el) return;
      const x = (e.clientX / window.innerWidth - 0.5) * 15;
      const y = (e.clientY / window.innerHeight - 0.5) * 15;
      el.style.transform = `translate3d(${x}px, ${y}px, 0)`;
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, [reduced]);

  useEffect(() => {
    const onScroll = () => { if (window.scrollY > 40) setFaded(true); };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <Section id="home" aria-labelledby="hero-heading">
      <Content>
        <Prompt aria-hidden="true">
          {reduced ? PROMPT_TEXT : typed}
          {!reduced && !isDone && <span className="caret" />}
        </Prompt>
        <Headline id="hero-heading">Fabio Lillman.</Headline>
        <Subtitle>
          Full stack dev who cares about <span className="accent">accessibility.</span>
        </Subtitle>
        <CTARow>
          <ButtonLink ref={primaryBtnRef} href="#work">
            See Work <Icon name="arrowRight" size={16} />
          </ButtonLink>
          <ButtonLink
            href={require('../assets/Fabio Lillman - CV.pdf')}
            $variant="ghost"
            download
          >
            <Icon name="download" size={16} /> Download CV
          </ButtonLink>
        </CTARow>
      </Content>

      <Orb>
        <TerminalFrame>
          <TitleBar>
            <TrafficLights>
              <CloseDot
                type="button"
                aria-label={sceneOpen ? 'Close scene' : 'Open scene'}
                aria-pressed={!sceneOpen}
                onClick={() => setSceneOpen((v) => !v)}
              />
              <Dot aria-hidden="true" />
              <Dot aria-hidden="true" />
            </TrafficLights>
            <TitleLabel aria-hidden="true">fabio@stockholm: ~/scene</TitleLabel>
          </TitleBar>
          <Screen>
            {sceneOpen ? (
              <Suspense
                fallback={
                  <LoadingState aria-hidden="true">
                    $ rendering scene<span className="caret" />
                  </LoadingState>
                }
              >
                <SplineWrap ref={orbRef} aria-hidden="true">
                  <LazySpline scene="https://prod.spline.design/uKdMNHzQ1yPovqGe/scene.splinecode" />
                </SplineWrap>
              </Suspense>
            ) : (
              <LoadingState aria-hidden="true">$ scene closed</LoadingState>
            )}
          </Screen>
        </TerminalFrame>
      </Orb>

      <ScrollCue $faded={faded} aria-hidden="true">scroll ↓</ScrollCue>
    </Section>
  );
};

export default Hero;
